import { useState, useEffect } from "react";
import { CheckCircle2, Copy, ExternalLink, Lock, Check, Rocket } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import confetti from "canvas-confetti";
import { generateFeedbackLink } from "@/utils/feedbackLinks";
import { supabase } from "@/integrations/supabase/client";

interface SuccessStepProps {
  pageSlug: string;
  companyName: string;
  onComplete: () => void;
}

export const SuccessStep = ({ pageSlug, companyName, onComplete }: SuccessStepProps) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [passcodeCopied, setPasscodeCopied] = useState(false);
  const [passcode, setPasscode] = useState<string | null>(null);

  const feedbackUrl = generateFeedbackLink(companyName, pageSlug);

  useEffect(() => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });
  }, []);

  useEffect(() => {
    const fetchPasscode = async () => {
      const { data } = await supabase
        .from("feedback_pages")
        .select("passcode")
        .eq("slug", pageSlug)
        .single();

      if (data?.passcode) {
        setPasscode(data.passcode);
      }
    };
    fetchPasscode();
  }, [pageSlug]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(feedbackUrl);
      setCopied(true);
      toast({
        title: "Link copied!",
        description: "Share this link with your team to start collecting feedback",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Please copy the link manually",
        variant: "destructive",
      });
    }
  };

  const handleCopyPasscode = async () => {
    if (!passcode) return;
    try {
      await navigator.clipboard.writeText(passcode);
      setPasscodeCopied(true);
      toast({ title: "Access code copied!" });
      setTimeout(() => setPasscodeCopied(false), 2000);
    } catch {
      toast({
        title: "Failed to copy",
        description: "Please copy the code manually",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="p-6 sm:p-10 text-center bg-editorial-paper">
      <div className="max-w-xl mx-auto space-y-8">
        {/* Eyebrow + headline */}
        <div className="space-y-4">
          <span className="text-[10px] uppercase tracking-[0.28em] font-bold font-editorial text-editorial-ink-muted block">
            № 001 · Workspace ready
          </span>
          <Rocket className="w-10 h-10 text-primary mx-auto" strokeWidth={1.5} />
          <h2 className="font-serif italic text-3xl md:text-5xl leading-[1.05] tracking-tight text-editorial-ink">
            You're all set, {companyName}.
          </h2>
          <p className="font-serif italic text-base text-editorial-ink-muted">
            Your workspace is ready. Start collecting feedback today.
          </p>
        </div>

        {/* Link card */}
        <div className="border border-editorial-ink/20 bg-editorial-paper-pure p-5 text-left">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold font-editorial text-editorial-ink-muted mb-3">
            Your feedback link
          </p>
          <div className="bg-editorial-paper border border-editorial-ink/15 p-3 mb-4">
            <code className="text-sm break-all text-editorial-ink font-mono">{feedbackUrl}</code>
          </div>

          {/* Passcode Display */}
          {passcode && (
            <div className="bg-editorial-paper border border-editorial-ink/30 p-3 mb-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-editorial-ink" />
                  <span className="text-[10px] uppercase tracking-[0.22em] font-bold font-editorial text-editorial-ink-muted">
                    Access code
                  </span>
                  <span className="font-mono font-bold text-editorial-ink text-lg">{passcode}</span>
                </div>
                <button
                  onClick={handleCopyPasscode}
                  className="min-h-[40px] px-3 inline-flex items-center gap-1.5 border border-editorial-ink/30 bg-editorial-paper text-editorial-ink text-[10px] uppercase tracking-[0.2em] font-bold font-editorial hover:bg-editorial-ink hover:text-editorial-paper transition-colors"
                >
                  {passcodeCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {passcodeCopied ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="text-xs text-editorial-ink-muted font-serif italic mt-2">
                Recipients need this code to submit feedback
              </p>
            </div>
          )}

          <div className="flex gap-2 justify-center flex-wrap">
            <button
              onClick={handleCopy}
              className="min-h-[44px] px-4 inline-flex items-center gap-2 border border-editorial-ink/30 bg-editorial-paper text-editorial-ink text-[11px] uppercase tracking-[0.2em] font-bold font-editorial hover:bg-editorial-ink hover:text-editorial-paper transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Link"}
            </button>
            <button
              onClick={() => window.open(`${feedbackUrl}?preview=true`, '_blank')}
              className="min-h-[44px] px-4 inline-flex items-center gap-2 border border-editorial-ink/30 bg-editorial-paper text-editorial-ink text-[11px] uppercase tracking-[0.2em] font-bold font-editorial hover:bg-editorial-ink hover:text-editorial-paper transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Preview
            </button>
          </div>
        </div>

        {/* What's next */}
        <div className="text-left border border-editorial-ink/15 bg-editorial-paper-pure p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-6 bg-editorial-ink"></div>
            <span className="text-[10px] uppercase tracking-[0.28em] font-bold font-editorial text-editorial-ink">
              What's next
            </span>
          </div>
          <div className="space-y-4">
            {[
              { title: `Share your link${passcode ? " & access code" : ""}`, hint: "Send via email, Slack, or print the QR code" },
              { title: "Collect anonymous feedback", hint: "All responses are 100% anonymous" },
              { title: "View AI-powered insights", hint: "Sentiment analysis, themes, and trends" },
            ].map((item) => (
              <div key={item.title} className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="font-serif italic text-base text-editorial-ink">{item.title}</div>
                  <div className="text-sm text-editorial-ink-muted font-editorial">
                    {item.hint}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={onComplete}
          className="w-full min-h-[52px] inline-flex items-center justify-center gap-2 bg-editorial-ink text-editorial-paper text-sm uppercase tracking-[0.2em] font-bold font-editorial hover:bg-primary transition-colors"
        >
          Go to Dashboard →
        </button>
      </div>
    </div>
  );
};
